import { useEffect } from 'react';

export interface PageMetadataOptions {
  title?: string;
  description?: string;
  ogTitle?: string;
  ogDescription?: string;
}

const DEFAULT_TITLE = 'Ciencuadras: Broker - Comercial';
const DEFAULT_DESCRIPTION = 'Portal comercial y de gestión inmobiliaria de Ciencuadras';

/**
 * Updates document title and meta tags dynamically based on active route or view.
 * Restores defaults on unmount or when options change.
 */
export function usePageMetadata(options: PageMetadataOptions, enabled: boolean = true) {
  useEffect(() => {
    if (!enabled) return;

    const previousTitle = document.title || DEFAULT_TITLE;

    // Helper to safely get or create meta tags
    const setMetaTag = (selector: string, attrKey: string, attrVal: string, content?: string): string | null => {
      if (!content) return null;
      let el = document.querySelector(selector) as HTMLMetaElement | null;
      const prev = el ? el.getAttribute('content') : null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attrKey, attrVal);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
      return prev;
    };

    // Update title
    if (options.title) {
      document.title = options.title;
    }

    // Update meta tags
    const prevDesc = setMetaTag('meta[name="description"]', 'name', 'description', options.description);
    const prevOgTitle = setMetaTag('meta[property="og:title"]', 'property', 'og:title', options.ogTitle || options.title);
    const prevOgDesc = setMetaTag('meta[property="og:description"]', 'property', 'og:description', options.ogDescription || options.description);

    return () => {
      // Revert title
      document.title = previousTitle || DEFAULT_TITLE;

      // Revert description
      const descEl = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
      if (descEl) {
        descEl.setAttribute('content', prevDesc || DEFAULT_DESCRIPTION);
      }

      // Revert og:title
      const ogTitleEl = document.querySelector('meta[property="og:title"]') as HTMLMetaElement | null;
      if (ogTitleEl) {
        ogTitleEl.setAttribute('content', prevOgTitle || DEFAULT_TITLE);
      }

      // Revert og:description
      const ogDescEl = document.querySelector('meta[property="og:description"]') as HTMLMetaElement | null;
      if (ogDescEl) {
        ogDescEl.setAttribute('content', prevOgDesc || DEFAULT_DESCRIPTION);
      }
    };
  }, [enabled, options.title, options.description, options.ogTitle, options.ogDescription]);
}
